// Get the summarizer service URL from environment variable, fallback to local proxy path
const SUMMARIZER_SERVICE_URL = import.meta.env.VITE_SUMMARIZER_SERVICE_URL || '/service-summarizer-stage'

export async function fetchCustomerSummary(customerId: string) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/customer?customerId=${encodeURIComponent(customerId)}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  if (!response.ok) throw new Error('Failed to fetch customer summary');
  return await response.json();
}

export async function fetchCustomerSummaryById(summaryId: string) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/customer/${encodeURIComponent(summaryId)}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('CUSTOMER_SUMMARY_NOT_FOUND');
    }
    throw new Error('Failed to fetch customer summary by ID');
  }
  return await response.json();
}

export async function fetchContactSummary(contactId: string) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/contact?contactId=${encodeURIComponent(contactId)}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('CONTACT_SUMMARY_NOT_FOUND');
    }
    throw new Error('Failed to fetch contact summary');
  }
  return await response.json();
}

export async function fetchContactSummaryById(summaryId: string) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/contact/${encodeURIComponent(summaryId)}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('CONTACT_SUMMARY_NOT_FOUND');
    }
    throw new Error('Failed to fetch contact summary by ID');
  }
  return await response.json();
}

export interface CaseSummary {
  summary: string;
  one_line_summary?: string;
  status: string;
  source: string;
  created_date: string;
  completed_date: string;
  sentiment?: {
    emotion: string;
    score: number;
    confidence: number;
    key_indicators?: string[];
  };
  issue_category?: string;
  resolution_status?: string;
  next_steps?: string[];
  case_data: {
    case_metadata: {
      id: string;
      sponsor_partners_teams_id: string;
      owner_partners_teams_id: string;
      source: string;
      collaborator_partners_teams_ids: string[];
      display_name: string;
      idr_isq: string;
      idr_process: any[];
      idr_resolution: any[];
      dicts: string[];
      canned_responses: any[];
      kbs: any[];
      categories: string[];
      type: number;
      escalated: boolean;
      claimed: string;
      created: string;
      customer: {
        id: string;
        name: string | null;
        email: string;
        phone: string | null;
        created: string;
      };
      location: {
        id: string;
        zipcode: string;
        phone: string | null;
        site_name: string;
        created: string;
      };
      customerUser: {
        id: string;
        first_name: string;
        last_name: string | null;
        email: string;
        sms_number: string;
        created: string;
      };
    };
    case_transcript: any[];
    case_id: string;
    org_id: string;
    source: string;
    summary: string | null;
  };
}

export async function fetchCaseSummary(caseId: string): Promise<CaseSummary[]> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/case?caseId=${encodeURIComponent(caseId)}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('CASE_SUMMARY_NOT_FOUND');
    }
    throw new Error('Failed to fetch case summary');
  }
  
  // Check if response has content before parsing
  const contentType = response.headers.get('content-type');
  const text = await response.text();
  
  if (!text || text.trim() === '') {
    // Return empty array for empty response
    return [];
  }
  
  if (!contentType?.includes('application/json')) {
    console.error('Unexpected content type:', contentType, 'Response text:', text);
    throw new Error('Invalid response format from server');
  }
  
  try {
    return JSON.parse(text);
  } catch (error) {
    console.error('Failed to parse JSON response:', text);
    throw new Error('Failed to parse case summary response');
  }
}

export interface CreateCaseSummaryParams {
  caseId: string;
  orgId: string;
}

export async function createCaseSummary(params: CreateCaseSummaryParams) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/case`, {
    headers: {
      'accept': '*/*',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      caseId: params.caseId,
      orgId: params.orgId,
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to create case summary');
  }
  
  return await response.json();
}

export interface CreateCustomerSummaryParams {
  orgId: string;
  customerId: string;
  customerName: string;
  startDate: string;
  endDate: string;
}

export async function createCustomerSummary(params: CreateCustomerSummaryParams) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/customer`, {
    headers: {
      'accept': '*/*',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      orgId: params.orgId,
      customerId: params.customerId,
      customerName: params.customerName,
      startDate: params.startDate,
      endDate: params.endDate,
    })
  });
  if (!response.ok) throw new Error('Failed to create customer summary');
  return await response.json();
}

export interface CreateContactSummaryParams {
  orgId: string;
  contactId: string;
  contactName: string;
  startDate: string;
  endDate: string;
}

export async function createContactSummary(params: CreateContactSummaryParams) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/contact`, {
    headers: {
      'accept': '*/*',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      orgId: params.orgId,
      contactId: params.contactId,
      contactName: params.contactName,
      startDate: params.startDate,
      endDate: params.endDate,
    })
  });
  if (!response.ok) throw new Error('Failed to create contact summary');
  return await response.json();
}

export async function fetchLocationSummary(locationId: string) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/location?locationId=${encodeURIComponent(locationId)}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('LOCATION_SUMMARY_NOT_FOUND');
    }
    throw new Error('Failed to fetch location summary');
  }
  return await response.json();
}

export async function fetchLocationSummaryById(summaryId: string) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/location/${encodeURIComponent(summaryId)}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('LOCATION_SUMMARY_NOT_FOUND');
    }
    throw new Error('Failed to fetch location summary by ID');
  }
  return await response.json();
}

export interface CreateLocationSummaryParams {
  orgId: string;
  locationId: string;
  locationName: string;
  startDate: string;
  endDate: string;
}

export async function createLocationSummary(params: CreateLocationSummaryParams) {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/location`, {
    headers: {
      'accept': '*/*',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      orgId: params.orgId,
      locationId: params.locationId,
      locationName: params.locationName,
      startDate: params.startDate,
      endDate: params.endDate,
    })
  });
  if (!response.ok) throw new Error('Failed to create location summary');
  return await response.json();
}

// OpenAI API Types and Functions
export interface Message {
  senderId: string;
  content: string;
}

export interface OpenAIResponse {
  result: string;
  model_used: string;
  tokens_used?: number;
  error?: string | null;
  is_fallback?: boolean;
}

export interface SummarizeConversationRequest {
  messages: Message[];
  orgId: string;
}

export interface GenerateReplyRequest {
  messages: Message[];
  orgId: string;
}

export interface ReformatEmailRequest {
  content: string;
  orgId: string;
}

export interface ReformatChatRequest {
  content: string;
  orgId: string;
}

export interface ReformatKnowledgeBaseRequest {
  content: string;
  orgId: string;
}

export interface CleanupEmailRequest {
  content: string;
  orgId: string;
}

export interface GeneralOpenAIRequest {
  model: string;
  messages: Array<{
    role: string;
    content: string;
  }>;
  orgId: string;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  stop?: string | string[];
}

// OpenAI API Functions
export async function summarizeConversation(request: SummarizeConversationRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/summarize-conversation`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      messages: request.messages,
      org_id: request.orgId
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to summarize conversation');
  }
  
  return await response.json();
}

export async function generateReply(request: GenerateReplyRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/generate-reply`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      messages: request.messages,
      org_id: request.orgId
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to generate reply');
  }
  
  return await response.json();
}

export async function reformatEmail(request: ReformatEmailRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/reformat-email`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      content: request.content,
      org_id: request.orgId
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to reformat email');
  }
  
  return await response.json();
}

export async function reformatChat(request: ReformatChatRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/reformat-chat`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      content: request.content,
      org_id: request.orgId
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to reformat chat response');
  }
  
  return await response.json();
}

export async function reformatKnowledgeBase(request: ReformatKnowledgeBaseRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/reformat-knowledge-base`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      content: request.content,
      org_id: request.orgId
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to reformat knowledge base article');
  }
  
  return await response.json();
}

export async function cleanupEmail(request: CleanupEmailRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/cleanup-email`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      content: request.content,
      org_id: request.orgId
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to cleanup email');
  }
  
  return await response.json();
}

export async function chatCompletion(request: GeneralOpenAIRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/chat-completion`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      model: request.model,
      messages: request.messages,
      org_id: request.orgId,
      ...(request.temperature !== undefined && { temperature: request.temperature }),
      ...(request.max_tokens !== undefined && { max_tokens: request.max_tokens }),
      ...(request.top_p !== undefined && { top_p: request.top_p }),
      ...(request.frequency_penalty !== undefined && { frequency_penalty: request.frequency_penalty }),
      ...(request.presence_penalty !== undefined && { presence_penalty: request.presence_penalty }),
      ...(request.stop !== undefined && { stop: request.stop })
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to complete chat request');
  }
  
  return await response.json();
}

export interface PassthroughOpenAIRequest {
  orgId: string;
  [key: string]: any;
}

export async function passthroughOpenAI(request: PassthroughOpenAIRequest): Promise<any> {
  const { orgId, ...rest } = request;
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/passthrough`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      ...rest,
      org_id: orgId
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to process OpenAI passthrough request');
  }
  
  return await response.json();
}

// Analytics API Types and Functions
export interface TraceStats {
  trace_name: string;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  avg_duration_ms: number | null;
  total_tokens: number | null;
  avg_tokens_per_run: number | null;
}

export interface OrgTracesAnalytics {
  org_id: string;
  date_range_start: string;
  date_range_end: string;
  total_traces: number;
  trace_stats: TraceStats[];
}

export async function fetchOrgTracesAnalytics(orgId: string, daysBack: number = 1): Promise<OrgTracesAnalytics> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/analytics/org/${encodeURIComponent(orgId)}/traces?days_back=${daysBack}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch org traces analytics');
  }
  
  return await response.json();
}

export async function fetchOrgTraceNames(orgId: string, daysBack: number = 1): Promise<string[]> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/analytics/org/${encodeURIComponent(orgId)}/trace-names?days_back=${daysBack}`, {
    headers: {
      'accept': 'application/json',
    },
    method: 'GET',
    credentials: 'omit',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch org trace names');
  }
  
  return await response.json();
}

export interface TraceListItem {
  trace_id: string;
  trace_name: string;
  timestamp: string;
  user_id: string;
  session_id: string | null;
  input_preview: string;
  output_preview: string;
  total_cost: number;
  latency_ms: number | null;
  tags: string[];
}

export interface TraceListResponse {
  org_id: string;
  trace_name: string;
  total_count: number;
  traces: TraceListItem[];
  next_cursor: string | null;
}

export async function fetchTraceList(
  orgId: string,
  traceName: string,
  daysBack: number = 7,
  limit: number = 50,
  cursor?: string
): Promise<TraceListResponse> {
  const params = new URLSearchParams({
    days_back: daysBack.toString(),
    limit: limit.toString(),
  });
  
  if (cursor) {
    params.append('cursor', cursor);
  }
  
  const response = await fetch(
    `${SUMMARIZER_SERVICE_URL}/analytics/org/${encodeURIComponent(orgId)}/traces/${encodeURIComponent(traceName)}/list?${params}`,
    {
      headers: {
        'accept': 'application/json',
      },
      method: 'GET',
      credentials: 'omit',
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch trace list');
  }
  
  return await response.json();
}

export interface TraceObservation {
  observation_id: string;
  type: string;
  name: string;
  start_time: string;
  end_time: string;
  input: any;
  output: any;
  metadata: Record<string, any>;
  model: string | null;
  usage: {
    unit: string;
    input: number;
    output: number;
    total: number;
  } | null;
  cost: number | null;
}

export interface TraceDetail {
  trace_id: string;
  trace_name: string;
  timestamp: string;
  user_id: string;
  session_id: string | null;
  input: any;
  output: any;
  metadata: Record<string, any>;
  tags: string[];
  total_cost: number;
  latency_ms: number | null;
  observations: TraceObservation[];
}

export async function fetchTraceDetail(
  orgId: string,
  traceName: string,
  traceId: string
): Promise<TraceDetail> {
  const response = await fetch(
    `${SUMMARIZER_SERVICE_URL}/analytics/org/${encodeURIComponent(orgId)}/traces/${encodeURIComponent(traceName)}/${encodeURIComponent(traceId)}`,
    {
      headers: {
        'accept': 'application/json',
      },
      method: 'GET',
      credentials: 'omit',
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch trace detail');
  }
  
  return await response.json();
}

// Scoring API Types and Functions
export type ScoreDataType = 'NUMERIC' | 'BOOLEAN' | 'CATEGORICAL';

export interface Score {
  id: string;
  name: string;
  value: number | null;
  data_type: ScoreDataType;
  timestamp: string;
  comment: string | null;
  source: string;
  author_user_id: string | null;
}

export interface TraceScoresResponse {
  trace_id: string;
  scores: Score[];
  score_count: number;
}

export interface SubmitNumericScoreRequest {
  trace_id: string;
  name: string;
  value: number;
  comment?: string;
  user_id: string;
}

export interface SubmitBooleanScoreRequest {
  trace_id: string;
  name: string;
  value: boolean;
  comment?: string;
  user_id: string;
}

export interface SubmitCommentRequest {
  trace_id: string;
  name: string;
  comment: string;
  user_id: string;
}

export interface SubmitScoreResponse {
  success: boolean;
  score_id: string;
  message: string;
}

export interface AggregatedScore {
  score_name: string;
  data_type: ScoreDataType;
  total_count: number;
  avg_value?: number;
  value_distribution?: Record<string, number>;
}

export async function fetchTraceScores(traceId: string): Promise<TraceScoresResponse> {
  const response = await fetch(
    `${SUMMARIZER_SERVICE_URL}/analytics/traces/${encodeURIComponent(traceId)}/scores`,
    {
      headers: {
        'accept': 'application/json',
      },
      method: 'GET',
      credentials: 'omit',
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch trace scores');
  }
  
  return await response.json();
}

export async function submitNumericScore(request: SubmitNumericScoreRequest): Promise<SubmitScoreResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/analytics/scores/numeric`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify(request),
  });
  
  if (!response.ok) {
    throw new Error('Failed to submit numeric score');
  }
  
  return await response.json();
}

export async function submitBooleanScore(request: SubmitBooleanScoreRequest): Promise<SubmitScoreResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/analytics/scores/boolean`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify(request),
  });
  
  if (!response.ok) {
    throw new Error('Failed to submit boolean score');
  }
  
  return await response.json();
}

export async function submitComment(request: SubmitCommentRequest): Promise<SubmitScoreResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/analytics/scores/comment`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify(request),
  });
  
  if (!response.ok) {
    throw new Error('Failed to submit comment');
  }
  
  return await response.json();
}

export async function fetchAggregatedScores(
  orgId: string,
  daysBack: number = 7,
  traceName?: string
): Promise<AggregatedScore[]> {
  const params = new URLSearchParams({
    days_back: daysBack.toString(),
  });
  
  if (traceName) {
    params.append('trace_name', traceName);
  }
  
  const response = await fetch(
    `${SUMMARIZER_SERVICE_URL}/analytics/org/${encodeURIComponent(orgId)}/scores/aggregated?${params}`,
    {
      headers: {
        'accept': 'application/json',
      },
      method: 'GET',
      credentials: 'omit',
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch aggregated scores');
  }
  
  return await response.json();
} 